import React, { Component } from 'react';
import { XIcon, AlertTriangleIcon } from 'lucide-react';
import Card from '../ui/Card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
interface WarrantyRootCauseViewProps {
  data: any;
  onClose: () => void;
}
const WarrantyRootCauseView: React.FC<WarrantyRootCauseViewProps> = ({
  data,
  onClose
}) => {
  const warrantyData = {
    totalClaims: 3563,
    totalCost: 629,
    rootCauses: [{
      cause: 'Manufacturing Defect',
      claims: 1245,
      cost: 218,
      percentage: 35
    }, {
      cause: 'Component Failure',
      claims: 892,
      cost: 156,
      percentage: 25
    }, {
      cause: 'Installation Error',
      claims: 623,
      cost: 109,
      percentage: 17
    }, {
      cause: 'Design Issue',
      claims: 445,
      cost: 78,
      percentage: 12
    }, {
      cause: 'Environmental',
      claims: 358,
      cost: 68,
      percentage: 11
    }],
    topIssues: [{
      product: 'LED Headlamp',
      issue: 'Voltage Fluctuation',
      claims: 142,
      cost: 42.5,
      trend: 'increasing'
    }, {
      product: 'Battery System',
      issue: 'Software Bug',
      claims: 98,
      cost: 35.2,
      trend: 'stable'
    }, {
      product: 'Sensor Module',
      issue: 'Dust Ingress',
      claims: 76,
      cost: 28.8,
      trend: 'decreasing'
    }],
    pieData: [{
      name: 'Manufacturing',
      value: 35,
      color: '#ef4444'
    }, {
      name: 'Component',
      value: 25,
      color: '#f59e0b'
    }, {
      name: 'Installation',
      value: 17,
      color: '#eab308'
    }, {
      name: 'Design',
      value: 12,
      color: '#3b82f6'
    }, {
      name: 'Environmental',
      value: 11,
      color: '#10b981'
    }]
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6" data-id="element-11479">
      <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto" data-id="element-11480">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between" data-id="element-11481">
          <div className="flex items-center" data-id="element-11482">
            <AlertTriangleIcon size={24} className="text-red-600 mr-3" data-id="element-11483" />
            <div data-id="element-11484">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-11485">
                Warranty Claims - Root Cause Analysis
              </h2>
              <p className="text-sm text-gray-600" data-id="element-11486">
                Detailed breakdown of warranty issues and costs
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-11487">
            <XIcon size={24} className="text-gray-500" data-id="element-11488" />
          </button>
        </div>
        {/* Content */}
        <div className="p-6 space-y-6" data-id="element-11489">
          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-4" data-id="element-11490">
            <Card className="p-4 bg-gradient-to-br from-red-50 to-white" data-id="element-11491">
              <div className="text-sm text-gray-600 mb-1" data-id="element-11492">Total Claims</div>
              <div className="text-3xl font-bold text-red-600" data-id="element-11493">
                {warrantyData.totalClaims.toLocaleString()}
              </div>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-orange-50 to-white" data-id="element-11494">
              <div className="text-sm text-gray-600 mb-1" data-id="element-11495">Total Cost</div>
              <div className="text-3xl font-bold text-orange-600" data-id="element-11496">
                ₹{warrantyData.totalCost} Cr
              </div>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-yellow-50 to-white" data-id="element-11497">
              <div className="text-sm text-gray-600 mb-1" data-id="element-11498">Avg Cost/Claim</div>
              <div className="text-3xl font-bold text-yellow-600" data-id="element-11499">
                ₹
                {(warrantyData.totalCost * 10000000 / warrantyData.totalClaims).toFixed(0)}
              </div>
            </Card>
          </div>
          {/* Root Causes */}
          <Card className="p-6" data-id="element-11500">
            <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-11501">
              Root Cause Distribution
            </h3>
            <div className="space-y-3" data-id="element-11502">
              {warrantyData.rootCauses.map((cause, index) => <div key={index} className="border border-gray-200 rounded-lg p-4" data-id="element-11503">
                  <div className="flex items-center justify-between mb-2" data-id="element-11504">
                    <span className="font-semibold text-gray-800" data-id="element-11505">
                      {cause.cause}
                    </span>
                    <div className="flex items-center space-x-4 text-sm" data-id="element-11506">
                      <span className="text-gray-600" data-id="element-11507">
                        {cause.claims} claims
                      </span>
                      <span className="font-semibold text-red-600" data-id="element-11508">
                        ₹{cause.cost} Cr
                      </span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-11509">
                    <div className="bg-red-600 h-2 rounded-full" style={{
                  width: `${cause.percentage}%`
                }} data-id="element-11510" />
                  </div>
                </div>)}
            </div>
          </Card>
          {/* Charts */}
          <div className="grid grid-cols-2 gap-6" data-id="element-11511">
            <Card className="p-6" data-id="element-11512">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-11513">
                Claims by Root Cause
              </h3>
              <ResponsiveContainer width="100%" height={300} data-id="element-11514">
                <PieChart data-id="element-11515">
                  <Pie data={warrantyData.pieData} cx="50%" cy="50%" labelLine={false} label={({
                  name,
                  value
                }) => `${name}: ${value}%`} outerRadius={100} fill="#8884d8" dataKey="value" data-id="element-11516">
                    {warrantyData.pieData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-11517" />)}
                  </Pie>
                  <Tooltip data-id="element-11518" />
                </PieChart>
              </ResponsiveContainer>
            </Card>
            <Card className="p-6" data-id="element-11519">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-11520">
                Cost by Root Cause (₹ Cr)
              </h3>
              <ResponsiveContainer width="100%" height={300} data-id="element-11521">
                <BarChart data={warrantyData.rootCauses} data-id="element-11522">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-11523" />
                  <XAxis dataKey="cause" tick={{
                  fontSize: 10
                }} angle={-15} textAnchor="end" height={80} data-id="element-11524" />
                  <YAxis tick={{
                  fontSize: 12
                }} data-id="element-11525" />
                  <Tooltip data-id="element-11526" />
                  <Bar dataKey="cost" fill="#ef4444" data-id="element-11527" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>
          {/* Top Issues */}
          <Card className="p-6" data-id="element-11528">
            <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-11529">
              Top 3 Critical Issues
            </h3>
            <div className="space-y-3" data-id="element-11530">
              {warrantyData.topIssues.map((issue, index) => <div key={index} className="border-l-4 border-red-500 bg-red-50 p-4 rounded" data-id="element-11531">
                  <div className="flex items-start justify-between" data-id="element-11532">
                    <div className="flex-1" data-id="element-11533">
                      <div className="font-bold text-gray-900 mb-1" data-id="element-11534">
                        {issue.product} - {issue.issue}
                      </div>
                      <div className="text-sm text-gray-600" data-id="element-11535">
                        {issue.claims} claims • ₹{issue.cost} Cr cost
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${issue.trend === 'increasing' ? 'bg-red-100 text-red-700' : issue.trend === 'stable' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`} data-id="element-11536">
                      {issue.trend}
                    </span>
                  </div>
                </div>)}
            </div>
          </Card>
          {/* Action Plan */}
          <Card className="p-6 bg-blue-50 border-2 border-blue-200" data-id="element-11537">
            <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-11538">
              Recommended Actions
            </h3>
            <ul className="space-y-2 text-sm" data-id="element-11539">
              <li className="flex items-start" data-id="element-11540">
                <span className="text-blue-600 mr-2" data-id="element-11541">1.</span>
                <span data-id="element-11542">
                  Implement enhanced quality checks for manufacturing processes
                  (35% of claims)
                </span>
              </li>
              <li className="flex items-start" data-id="element-11543">
                <span className="text-blue-600 mr-2" data-id="element-11544">2.</span>
                <span data-id="element-11545">
                  Conduct supplier audit for component quality (25% of claims)
                </span>
              </li>
              <li className="flex items-start" data-id="element-11546">
                <span className="text-blue-600 mr-2" data-id="element-11547">3.</span>
                <span data-id="element-11548">
                  Improve installation training and documentation (17% of
                  claims)
                </span>
              </li>
            </ul>
          </Card>
        </div>
      </div>
    </div>;
};
export default WarrantyRootCauseView;